import React, { useState } from 'react';
import { motion } from 'motion/react';
import Button from "~/ui-apps/design-system/atoms/Button";
import { Mail, Send } from 'lucide-react';
import axios from 'axios';

const ContactSection: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [errors, setErrors] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    setErrors((prev) => ({
      ...prev,
      [name]: '',
    }));
    setSubmitMessage('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Validate form
    const newErrors = {
      name: formData.name.trim() === '' ? 'Name is required' : '',
      email: formData.email.trim() === ''
        ? 'Email is required'
        : !validateEmail(formData.email)
          ? 'Please enter a valid email address'
          : '',
      message: formData.message.trim() === '' ? 'Message is required' : '',
    };

    setErrors(newErrors);

    // Check if there are any errors
    if (Object.values(newErrors).some(error => error !== '')) {
      setIsSubmitting(false);
      return;
    }

    setSubmitMessage('');

    try {
      const response = await axios.post('/api/email/contact', {
        name: formData.name.trim(),
        email: formData.email.trim(),
        message: formData.message.trim(),
      });

      if (response.status === 200) {
        setIsSubmitted(true);
        setSubmitMessage("Thank you for reaching out! We'll get back to you soon.");
        setFormData({ name: '', email: '', message: '' });
      }
    } catch (error) {
      const message = axios.isAxiosError(error) && error.response?.data
        ? (error.response.data.message || error.response.data.error)
        : null;
      setSubmitMessage(message || 'An error occurred while sending your message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="scroll-mt-20 py-12 px-6 lg:scroll-mt-24"
      style={{ background: "#fcfcf9" }}
    >
      <div className="max-w-[1400px] mx-auto">
        <motion.div
          className="text-center mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-xs tracking-widest mb-4" style={{ fontWeight: '600', color: 'rgba(0,0,0,0.6)' }}>
            CONTACT
          </div>
          <h2
            className="tracking-tight mb-4"
            style={{
              fontSize: "clamp(2rem, 5vw, 3.5rem)",
              lineHeight: "1.1",
              fontWeight: "700",
              letterSpacing: "-0.02em",
            }}
          >
            Get in touch
          </h2>
        </motion.div>

        <motion.div
          className="max-w-[600px] mx-auto"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div
            className="rounded-3xl p-4 lg:p-12"
            style={{ background: "#ffffff", boxShadow: "0 0 0 1px rgba(0,0,0,0.06), 0 0 0 3px rgba(0,0,0,0.03)" }}
          >
            {isSubmitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                className="text-center py-8"
              >
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-white" />
                </div>
                <p className="text-lg font-semibold mb-2">Message sent!</p>
                <p className="text-black/60">{submitMessage}</p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`block w-full rounded-xl border-0 py-3 px-4 text-gray-900 shadow-sm ring-1 ring-inset ${errors.name ? 'ring-red-500' : 'ring-gray-300'
                      } placeholder:text-gray-400 placeholder:font-normal focus:ring-2 focus:ring-inset ${errors.name ? 'focus:ring-red-500' : 'focus:ring-black'
                      } sm:text-base sm:leading-6 transition-all`}
                    placeholder="Your name"
                  />
                  {errors.name && (
                    <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                  )}
                </div>

                <div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`block w-full rounded-xl border-0 py-3 px-4 text-gray-900 shadow-sm ring-1 ring-inset ${errors.email ? 'ring-red-500' : 'ring-gray-300'
                      } placeholder:text-gray-400 placeholder:font-normal focus:ring-2 focus:ring-inset ${errors.email ? 'focus:ring-red-500' : 'focus:ring-black'
                      } sm:text-base sm:leading-6 transition-all`}
                    placeholder="your.email@example.com"
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                  )}
                </div>

                <div>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    className={`block w-full rounded-xl border-0 py-3 px-4 text-gray-900 shadow-sm ring-1 ring-inset ${errors.message ? 'ring-red-500' : 'ring-gray-300'
                      } placeholder:text-gray-400 placeholder:font-normal focus:ring-2 focus:ring-inset ${errors.message ? 'focus:ring-red-500' : 'focus:ring-black'
                      } sm:text-base sm:leading-6 transition-all resize-none`}
                    placeholder="Tell us what's on your mind..."
                  />
                  {errors.message && (
                    <p className="text-red-500 text-sm mt-1">{errors.message}</p>
                  )}
                </div>

                <div className="flex justify-center mt-4">
                  <Button
                    type="primary"
                    size="medium"
                    disabled={isSubmitting}
                    icon={<Send className="w-4 h-4" />}
                    iconPosition="right"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </div>

                {submitMessage && !isSubmitted && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center text-sm text-red-600 mt-2"
                  >
                    {submitMessage}
                  </motion.p>
                )}
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactSection;

